import Vector from '../vector/vector'
import moment from 'moment'
import dayjs from 'dayjs'
import { abs, ceil, cos, floor, max, min, pow, round, sin, sqrt } from 'mathjs'

/**
 * Recipe intervals for representing durations.
 */
class RecipeInterval {
  constructor (x) {
    this.duration = moment.duration(x)
  }

  __call (name, args) {
    if (name in this) {
      return this[name](...args)
    }
    // Forward to moment.duration
    return this.duration[name].apply(this.duration, args)
  }

  __get (name) {
    const getterFunc = `get${this._capitalize(name)}Attribute`
    if (getterFunc in this) {
      // Forward to getter function
      return this[getterFunc]()
    }
    if (name in this) {
      return this[name]
    }
    // Forward to moment.duration
    return this.duration[name]
  }

  getHoursAttribute () {
    return this.duration.hours()
  }

  getMinutesAttribute () {
    return this.duration.minutes()
  }

  getSecondsAttribute () {
    return this.duration.seconds()
  }

  getTotalSecondsAttribute () {
    return this.totalSeconds()
  }

  /**
   * @param {string} time
   */
  fromTime (time) {
    return new RecipeInterval(time)
  }

  totalHours () {
    return this.duration.asHours()
  }

  totalMinutes () {
    return this.duration.asMinutes()
  }

  totalSeconds () {
    return this.duration.asSeconds()
  }

  /**
   * @param {string} string
   * @return {string}
   * @private
   */
  _capitalize (string) {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }
}

/**
 * Recipe date for representing date(time)s.
 */
class RecipeDate {
  dateObject

  constructor (date) {
    this.dateObject = dayjs(date)
  }

  toString () {
    return this.dateObject.toString()
  }

  valueOf () {
    return this.dateObject.valueOf()
  }

  /**
   * @return {string}
   */
  toISODateString () {
    return this.dateObject.format('YYYY-MM-DD')
  }

  addMonths (x) {
    this.dateObject = this.dateObject.add(x, 'month')
    return this
  }

  firstOfMonth () {
    this.dateObject = this.dateObject.startOf('month')
    return this
  }

  lastOfMonth () {
    this.dateObject = this.dateObject.endOf('month')
    return this
  }

  addDays (x) {
    this.dateObject = this.dateObject.add(x, 'day')
    return this
  }

  /**
   * @return {boolean}
   */
  isWeekday () {
    return [1, 2, 3, 4, 5].includes(this.dateObject.day())
  }

  addWeekdays (x) {
    const numericDirection = x < 0 ? -1 : 1
    let daysRemaining = abs(x)

    while (daysRemaining > 0) {
      this.addDays(numericDirection)

      if (this.isWeekday()) daysRemaining -= 1
    }

    return this
  }

  addWeekday () {
    return this.addWeekdays(1)
  }

  subWeekdays (x) {
    return this.addWeekdays(-x)
  }

  subWeekday () {
    return this.subWeekdays(1)
  }

  nextWeekday () {
    const searchLimit = 7
    let loopIndex = 1
    while (loopIndex < searchLimit) {
      this.addDays(1)

      if (this.isWeekday()) break
      loopIndex += 1
    }

    return this
  }

  previousWeekday () {
    const searchLimit = 7
    let loopIndex = 1
    while (loopIndex < searchLimit) {
      this.addDays(1)

      if (this.isWeekday()) break
      loopIndex += 1
    }

    return this
  }

  format (format) {
    return this.dateObject.format(format)
  }
}

class RecipeJson {
  encode (x) {
    return JSON.stringify(x)
  }

  decode (x) {
    return JSON.parse(x)
  }
}

const sum = x => {
  if (x && 'sum' in x) {
    return x.sum()
  }
  const unsafe = (x || []).reduce((a, b) => a + b, 0)
  return round(unsafe * 100) / 100
}

const avg = x => {
  if (x && 'avg' in x) {
    return x.avg()
  }
  return x ? x.reduce((a, b) => a + b, 0) / x.length : 0
}

const slugify = (text, separator) => {
  if (typeof separator === 'undefined') separator = '-'

  // Convert all dashes/underscores into separator
  const flip = separator === '-' ? '_' : '-'
  text = text.replace(flip, separator)

  // Remove all characters that are not the separator, letters, numbers, or whitespace.
  text = text.toLowerCase().replace(new RegExp('[^a-z0-9' + separator + '\\s]', 'g'), '')

  // Replace all separator characters and whitespace by a single separator
  text = text.replace(new RegExp('[' + separator + '\\s]+', 'g'), separator)

  return text.replace(new RegExp('^[' + separator + '\\s]+|[' + separator + '\\s]+$', 'g'), '')
}

export default {
  sum: x => sum(x),
  avg: x => avg(x),
  min: x => x.filter(k => k !== null && k !== undefined).length > 0 ? min(...x.filter(k => k !== null && k !== undefined)) : 0,
  max: x => x.filter(k => k !== null && k !== undefined).length > 0 ? max(...x.filter(k => k !== null && k !== undefined)) : 0,
  floor: floor,
  round: round,
  ceil: ceil,
  sin: sin,
  cos: cos,
  pow: pow,
  sqrt: sqrt,
  abs: abs,
  date: x => x ? new RecipeDate(x) : new RecipeDate(),
  today: _ => new RecipeDate().toISODateString(),
  duration: x => new RecipeInterval(x),
  relation: obj => { return { id: obj.id, module_id: obj.module_id } },
  join: (glue, list) => list.join(glue),
  len: countable => countable.length,
  vector: (...x) => new Vector(...x),
  replace: (search, replace, subject) => subject.replace(search, replace),
  slugify: slugify,
  json: new RecipeJson(),
}
